// ** React Imports
import { Fragment, useEffect, useRef, useState } from "react";

// ** MUI Components
import {
  IconButton,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from "@mui/material";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";

// Custom Component Imports
import CustomTextField from '../../../components/mui/text-field';

// ** Icon Imports
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import Icon from '../../../components/icon'
import FormHelperText from '@mui/material/FormHelperText'

// ** Third Party Imports
import PhoneInput from 'react-phone-input-2';
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { dev_baseUrl } from "utils/var";

const MySwal = withReactContent(Swal);


const AddContactDialog = ({
    open,
    handleClose,
    // handleDeletePhoneField,
    // handleSetPrimary,
    fetchContactData,
    customer,
    toast,
    loading,
    setLoading,
    contacts
}) => {
  const theme = useTheme();
  const idCounter = useRef(1);

  const [phoneFields, setPhoneFields] = useState([
    { id: idCounter.current++, value: "", primary: true },
  ]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    jobTitle: "",
    email: "",
  });

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (message || Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        setMessage('');        
        setErrors({});
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message, errors]);

  const handleAddPhoneField = () => {
    setPhoneFields([
      ...phoneFields,
      { id: idCounter.current++, value: "", primary: false },
    ]);
  };

  const ResetContactDialog = () => {
    setFormData({
      firstName: "",
      lastName: "",
      jobTitle: "",
      email: "",
    });
    setPhoneFields([{ id: idCounter.current++, value: "", primary: false }]);
  }

  const ChildHandleClose = () => {
    ResetContactDialog();
    handleClose();
  }

 const handleDeletePhoneField = (id) => {
  MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
    width: '390px',
    padding: '1rem',
    focusConfirm: false,
    showCloseButton: true,
    closeButtonHtml: '&times;',
    customClass: {
     popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
          cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
    }
  }).then((result) => {
    if (result.isConfirmed) {
      if (phoneFields.length > 1) {
        const updatedPhoneFields = phoneFields.filter((field) => field.id !== id);

        // If the deleted field was primary, make the first one primary
        if (phoneFields.find((field) => field.id === id)?.primary) {
          updatedPhoneFields[0].primary = true;
        }

        setPhoneFields(updatedPhoneFields);
      }
    }
  });
};

  
  // Function to set a specific phone field as primary
  const handleSetPrimary = (id) => {
    setPhoneFields((prevFields) =>
      prevFields.map((field) =>
        field.id === id
          ? { ...field, primary: true }
          : { ...field, primary: false }
      )
    );
  };


  const handlePhoneChange = (index, value) => {
    const updatedFields = phoneFields.map((field, idx) =>
      idx === index ? { ...field, value } : field
    );
    setPhoneFields(updatedFields);
  };

  const handleFormDataChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateName = (name, field) => {
    const errors = {};
    const forbiddenKeywords = ["Select", "Update", "Delete", "INSERT", "Drop"];
    
    const nameRegex = /^[A-Za-z\s'-]+$/;
    if (!nameRegex.test(name)) {
      // errors[field] = `${field === "firstName" ? "First" : "Last"} Name cannot contain special characters.`;
      if (field === "firstName" || field === "lastName") {
        errors[field] = `${field === "firstName" ? "First" : "Last"} Name cannot contain special characters.`;
      } else if (field === "jobTitle") {
        errors[field] = "Job Title cannot contain special characters.";
      }
    }
  
    if (forbiddenKeywords.some((word) => name.toLowerCase().includes(word.toLowerCase()))) {
      errors[field] = `Invalid Input.`;
    }
  
    if ((name && name.trim().length) < 2 || (name && name.trim().length) > 50) {
      errors[field] = `${field === "firstName" ? "First" : "Last"} Name must be between 2 and 50 characters.`;
    }
  
    if (!name.trim()) {
      // errors[field] = `${field === "firstName" ? "First" : "Last"} Name cannot be empty or only spaces.`;
      if (field === "firstName" || field === "lastName") {
        errors[field] = `${field === "firstName" ? "First" : "Last"} Name cannot be empty or only spaces.`;
      } else if (field === "jobTitle") {
        errors[field] = "Job Title cannot be empty or only spaces.";
      }
    }  

    if (!name) {
      // errors[field] = `${field === "firstName" ? "First" : "Last"} Name is required.`;
      if (field === "firstName" || field === "lastName") {
        errors[field] = `${field === "firstName" ? "First" : "Last"} Name is required.`;
      } else if (field === "jobTitle") {
        errors[field] = "Job Title is required.";
      }
    }

    return errors;
  };  

    const validatePhone = (value, index, existingErrors = {}) => {
    const errors = { ...existingErrors };

    if (!value) {
      errors[`phone_${index}`] = 'Phone number is required.';
    }
    else if (value.startsWith('1')) {
      errors[`phone_${index}`] = ' Area code cannot begin with 1.';
    }
    else if (!/^\d{10}$/.test(value)) {
      errors[`phone_${index}`] = 'Invalid phone number.';
    }
    else {
      delete errors[`phone_${index}`];
    }

    return errors;
  };

  const handleSubmit = async () => {

    setLoading(true);
    const newErrors = {};
    const seenPhones = new Set();
    
    phoneFields.forEach((field, index) => {
      const phone = field.value.trim();      
  
      if (!phone) {
        newErrors[`phone_${index}`] = 'Phone number is required.';
      } else if (phone.length < 10) {
        newErrors[`phone_${index}`] = 'Invalid phone number. It should be at least 10 digits.';
      } else if (phone === '1') {
        newErrors[`phone_${index}`] = 'Invalid phone number.';
      } else if (phone.length === 10) {
        if (seenPhones.has(phone)) {
          newErrors[`phone_${index}`] = 'Duplicate phone number detected.';
        } else {
          seenPhones.add(phone);
        }
      } else {
        newErrors[`phone_${index}`] = 'Phone number should be 10 digits';
      }
    });

    const firstNameErrors = validateName(formData.firstName, "firstName");
    if (Object.keys(firstNameErrors).length > 0) {
      newErrors.firstName = firstNameErrors.firstName;
    }

    const lastNameErrors = validateName(formData.lastName, "lastName");
    if (Object.keys(lastNameErrors).length > 0) {
      newErrors.lastName = lastNameErrors.lastName;
    }

    const jobTitleErrors = validateName(formData.jobTitle, "jobTitle");
    if (Object.keys(jobTitleErrors).length > 0) {
      newErrors.jobTitle = jobTitleErrors.jobTitle;
    }
    
    if ((!formData.email)) {      
      newErrors.email = 'Email is required.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email.';
    }

    const isDuplicate = Array.isArray(contacts) && contacts.some(
      (contact) => 
        contact.firstName.toLowerCase() === formData.firstName.toLowerCase() && 
        contact.lastName.toLowerCase() === formData.lastName.toLowerCase()
    );

    if(isDuplicate) newErrors.main = 'Contact with this name already exists.';


    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setLoading(false);
      return;
    }    

    const token = localStorage.getItem('jwtToken');

    if (!token) {
      navigate('/login', { state: { message: 'Session expired, please login again.' } });
      return;
    }

    try {

      const fetchContactsResponse = await fetch(`${dev_baseUrl}/Contact/getContact/0/${customer.customerID}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      let isPrimary = false;
      if (fetchContactsResponse.ok) {
        const con = await fetchContactsResponse.json();        
        isPrimary = con?.result?.length === 0;
      } else {
        console.error("Failed to fetch contacts");
      }

      const phoneNumbers = phoneFields.map((field, index) => {
        if (index === 0 && !phoneFields.some((f) => f.primary)) {
          return { phoneNo: field.value, isPrimary: true };
        }
        return { phoneNo: field.value, isPrimary: field.primary };
      });

      const contactData = [{
        customerID: customer.customerID,
        firstName: formData.firstName,
        lastName: formData.lastName,
        jobTitle: formData.jobTitle,
        email: formData.email,
        isPrimary: isPrimary,
        status: "active",
        phoneNumbersJSON: JSON.stringify(phoneNumbers),
        phoneNumbers: phoneNumbers,
      }];

      const response = await fetch(`${dev_baseUrl}/Contact/saveContact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Add token if required
        },
        body: JSON.stringify(contactData),
      });
      
      const data = await response.json();

      console.log(data);
      if (response.ok  && !data?.isError) {
        
        
        toast.success(data.message || 'Contact created successfully.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",        
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
          fetchContactData(customer?.customerID);

          ChildHandleClose();
      } else {
        toast.error(data?.result[0]?.failureReason || 'Failed to create contact', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",    
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
        // console.error("Failed to create contact");
      }
    } catch (error) {
      console.error("Error creating contact:", error);
    }finally{
      setLoading(false);
    }
  };

    return (        
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={open}
        onClose={ChildHandleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle sx={{borderBottom: '1px solid #D9D9D9', paddingBottom: '20px !important'}}>
          <Typography variant="h3" fontWeight='bold'>Create Contact</Typography>
        </DialogTitle>
        <DialogContent>
            {errors.main && (
              <FormHelperText sx={{ color: 'error.main', marginBottom: '15px', fontSize: '13px' , textAlign:'center'}}>
                {errors.main}
              </FormHelperText>
            )}
          <Grid container spacing={5}>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label="First Name"
                placeholder="John"
                required
                fullWidth
                name="firstName" 
                value={formData.firstName}
                onChange={handleFormDataChange}
                error={!!errors.firstName}
                helperText={errors.firstName}                
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label="Last Name"
                placeholder="Doe"
                required
                fullWidth
                name="lastName"
                value={formData.lastName}
                onChange={handleFormDataChange}
                error={!!errors.lastName}
                helperText={errors.lastName}                 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label="Job Title"
                placeholder="VP Facilities"
                required
                fullWidth
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleFormDataChange}
                error={!!errors.jobTitle}
                helperText={errors.jobTitle}                 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label="Email"
                placeholder="cleri@example.com"
                required
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleFormDataChange}
                error={!!errors.email}
                helperText={errors.email}                   
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Grid item xs={12} sm={6}>
                  <FormLabel
                    required
                    sx={{
                      fontSize: "13px",
                      color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                      "& .MuiFormLabel-asterisk": {
                        color: "error.main",
                      },
                    }}
                  >
                    Phone
                  </FormLabel>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/* {phoneFields.map((field) => (
                    <Box
                      key={field.id}
                      sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                    >
                      <PhoneInput
                        placeholder="+1 (000) 000-0000"
                        label={`Phone # ${field.id}`}
                        country={'us'}
                        onlyCountries = {['us']}
                        regions={'america'}
                        countryCodeEditable={false}
                        value={field.value}
                        // onChange={phone => setPhone(phone)}
                        // error={!!errors.lastName}
                        // helperText={'Error'} 
                        containerStyle={{
                          width: '100%',
                          margin: 0,
                        }}
                        inputStyle={{
                          width: '100%',
                          height: '39px', // Adjust based on your other input fields' height
                          // borderColor: errors.phone ? 'red' : '#ced4da', // Error handling for border color
                          borderColor: '#ced4da', // Error handling for border color
                          borderRadius: '4px',
                          paddingLeft: '48px', // To account for the flag icon
                          backgroundColor: '#fff',
                          color: 'rgba(47, 43, 61, 0.68)',
                        }} 
                      />
                      <IconButton
                        color="secondary"
                        onClick={() => handleSetPrimary(field.id)}
                        disabled={field.primary}
                      >
                        {field.primary ? (
                          <StarIcon sx={{ color: "#E7D557" }} />
                        ) : (
                          <StarOutlineIcon />
                        )}
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => handleDeletePhoneField(field.id)}
                        disabled={field.primary}
                      >
                        <Icon icon="tabler:trash" />
                      </IconButton>
                    </Box>
                  ))} */}
                   {phoneFields.map((field, index) => (
                    <>
                    <Box
                      key={field.id}
                      sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                    >
                      <PhoneInput
                        inputClass="custom-phone-input"
                        placeholder="(000) 000-0000"
                        country={"us"}
                        onlyCountries={["us"]}
                        value={field.value || ''}
                        disableDropdown	= {true}
                        regions={'america'}
                        disableCountryCode={true}
                       onChange={(value) => {
                      setPhoneFields(prevFields =>
                        prevFields.map(f =>
                          f.id === field.id ? { ...f, value } : f
                        )
                      );

                      setErrors(prevErrors =>
                        validatePhone(value, index, prevErrors)
                      );
                    }}
                        containerStyle={{ width: "100%", margin: 0 }}
                        inputStyle={{
                          width: "100%",
                          height: "39px",
                          borderColor: "rgba(208, 212, 241, 0.2)",
                          borderRadius: "4px",
                          paddingLeft: "48px",
                          backgroundColor: theme.palette.mode === 'dark' ? "#1B2431" : "#fff",
                          color: theme.palette.mode === 'dark' ? "rgba(208, 212, 241, 0.68)" : "rgba(47, 43, 61, 0.68)",
                        }}
                      />                      
                      <IconButton
                        onClick={() => handleSetPrimary(field.id)}
                        disabled={field.primary}
                      >
                        {field.primary ? <StarIcon sx={{ color: "#E7D557" }} /> : <StarOutlineIcon />}
                      </IconButton>
                      <IconButton onClick={() => handleDeletePhoneField(field.id)} disabled={field.primary}>
                        <Icon icon="tabler:trash" />
                      </IconButton>                   
                    </Box>
                     {errors[`phone_${index}`] && (
                      <FormHelperText sx={{ color: 'error.main', marginTop: '4px', fontSize: '13px' }}>
                        {errors[`phone_${index}`]}
                      </FormHelperText>
                    )}
                    </>
                  ))}
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleAddPhoneField}
                    sx={{mt: 2}}
                  >
                    Add Phone
                  </Button>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                padding: '20px 24px !important', 
                boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`, 
                zIndex: 1 
            }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button
              variant="contained"
              color="info"
              onClick={ChildHandleClose}
            >
              Cancel
            </Button>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="main"
              onClick={handleSubmit}
            >
              Create Contact
            </Button>
          </Box>
        </DialogActions>
      </Dialog> 
                
      )
}

export default AddContactDialog